/**
 * Demo ventes — a realistic ~45-day sales history for the BETA experience.
 *
 * The dashboard KPIs, 30-day revenue trend, top-products, category split,
 * CHIFA share and caisse totals are all DERIVED from this array, so a rich,
 * believable history here is what makes the whole demo feel like a real,
 * busy pharmacy. Generation is deterministic (seeded PRNG — no Math.random)
 * so every session sees the same numbers.
 *
 * Still covers every type (LIBRE / CHIFA / INSTANCE) and statut
 * (EN_COURS / VALIDEE / ANNULEE / RETOURNEE) via showcase rows at the end,
 * so list filters and the detail drawer always have something to show.
 */

// Deterministic PRNG (mulberry32) — stable demo numbers across reloads.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** ISO timestamp `daysAgo` days back, at the given hour/min (local-ish). */
function atDayHour(daysAgo, hour, min) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
}

export function buildVentes(produits, stockLots, clients, users) {
  const caissier1 = users.find((u) => u.id === "beta-user-caissier-1") || users[2];
  const caissier2 = users.find((u) => u.id === "beta-user-caissier-2") || users[3];
  const caissiers = [caissier1, caissier2].filter(Boolean);
  const caissierProj = (u) => ({ id: u.id, nom: u.nom, prenom: u.prenom });
  const findProd = (id) => produits.find((p) => p.id === id);
  const findLot = (pid) =>
    stockLots.find((l) => l.produit_id === pid && l.quantite_actuelle > 0)?.id ||
    null;

  // CHIFA-eligible clients (have a card) for tiers-payant sales.
  const chifaClients = clients.filter((c) => c.carte_chifa);
  const allClients = clients;

  // Weighted product pools. Fast-movers (OTC / common) sell often; chronic /
  // expensive meds sell less but lift average basket value; para/dispositif
  // round out the category mix so "ventes par catégorie" looks real.
  const ID = (p) => p.id;
  const fastMovers = produits
    .filter((p) =>
      ["p-001", "p-002", "p-010", "p-020", "p-021", "p-022", "p-024", "p-025", "p-027", "p-030", "p-036", "p-041", "p-045"].includes(p.id),
    )
    .map(ID);
  const chronic = produits
    .filter((p) =>
      ["p-005", "p-006", "p-008", "p-012", "p-013", "p-014", "p-015", "p-016", "p-017", "p-037", "p-039", "p-040"].includes(p.id),
    )
    .map(ID);
  const premium = produits
    .filter((p) => ["p-018", "p-033", "p-034", "p-042", "p-043", "p-044"].includes(p.id))
    .map(ID);
  const para = produits
    .filter((p) => String(p.id).startsWith("p-2") || String(p.id).startsWith("p-3"))
    .map(ID);
  // Fallbacks so generation never breaks if a pool is empty.
  const ensure = (arr, fb) => (arr.length ? arr : fb);
  const POOL_FAST = ensure(fastMovers, produits.slice(0, 8).map(ID));
  const POOL_CHRON = ensure(chronic, produits.slice(0, 8).map(ID));
  const POOL_PREM = ensure(premium, produits.slice(0, 4).map(ID));
  const POOL_PARA = ensure(para, produits.slice(0, 6).map(ID));

  const rng = mulberry32(20260530);
  const pick = (arr) => arr[Math.floor(rng() * arr.length) % arr.length];
  const chance = (p) => rng() < p;
  const between = (a, b) => a + Math.floor(rng() * (b - a + 1));

  let counter = 1;
  const ventes = [];

  const mkVente = (type, statut, dateIso, clientObj, items, opts = {}) => {
    const id = `vente-${String(counter).padStart(4, "0")}`;
    const num = `V-2026-${String(counter).padStart(5, "0")}`;
    counter += 1;
    const details = items
      .map((it, i) => {
        const p = findProd(it.produit_id);
        if (!p) return null;
        const qte = it.quantite;
        const pu = p?.ppa ?? 0;
        const remise = it.remise_pct ?? 0;
        const ttc = +(qte * pu * (1 - remise / 100)).toFixed(2);
        const pec = type === "CHIFA" && p.is_remboursable ? +(ttc * 0.8).toFixed(2) : 0;
        return {
          id: `${id}-det-${i + 1}`,
          produit_id: p.id,
          produit: {
            id: p.id,
            nom_commercial: p.nom_commercial,
            ppa: p.ppa,
            dci: p.dci,
            forme_pharma: p.forme_pharma,
            dosage: p.dosage,
          },
          stock_lot_id: findLot(p.id),
          quantite: qte,
          prix_unitaire: pu,
          remise_pct: remise,
          tva_pct: p.tva_pct ?? 0,
          montant_ligne_ttc: ttc,
          montant_pec_chifa: pec,
          montant_patient: +(ttc - pec).toFixed(2),
        };
      })
      .filter(Boolean);
    if (details.length === 0) {
      // Guarantee at least one line so totals never come out 0.
      counter -= 1;
      return null;
    }
    const totalTtc = +details.reduce((s, d) => s + d.montant_ligne_ttc, 0).toFixed(2);
    const totalPec = +details.reduce((s, d) => s + d.montant_pec_chifa, 0).toFixed(2);
    const c = opts.caissier || caissier1;
    return {
      id,
      numero_interne: num,
      type_vente: type,
      statut,
      date_vente: dateIso,
      client_id: clientObj ? clientObj.id : null,
      client: clientObj
        ? {
            id: clientObj.id,
            nom: clientObj.nom,
            prenom: clientObj.prenom,
            telephone: clientObj.telephone,
            carte_chifa: clientObj.carte_chifa,
          }
        : null,
      caissier_id: c.id,
      caissier: caissierProj(c),
      caisse_id: opts.caisse_id || "caisse-001",
      remise_montant: 0,
      remise_pct: opts.remise_pct || 0,
      montant_total_ht: +(totalTtc / 1.19).toFixed(2),
      montant_total_tva: +(totalTtc - totalTtc / 1.19).toFixed(2),
      montant_total_ttc: totalTtc,
      montant_pec_chifa: totalPec,
      montant_a_payer: +(totalTtc - totalPec).toFixed(2),
      mode_paiement: opts.mode || (chance(0.25) ? "TPE" : "ESPECES"),
      notes: opts.notes || "",
      details,
      carte_chifa_retenue: opts.carte_chifa_retenue || false,
      created_at: dateIso,
      updated_at: dateIso,
    };
  };

  // Build a basket of weighted lines. `n` is the requested line count but we
  // bias the product mix HEAVILY toward cheap OTC fast-movers and keep
  // quantities small, so the average basket lands in the realistic Algerian
  // officine range (~1 000–1 800 DA) rather than being inflated by chronic /
  // premium drugs. CHIFA baskets skew to reimbursable chronic meds (that's
  // what tiers-payant is for) but still stay modest.
  const basket = (n, { chifa = false } = {}) => {
    const lines = [];
    const used = new Set();
    for (let i = 0; i < n; i += 1) {
      const roll = rng();
      let pool;
      if (chifa) {
        // tiers-payant: mostly chronic reimbursables, some fast-movers, rare premium
        pool = roll < 0.6 ? POOL_CHRON : roll < 0.9 ? POOL_FAST : POOL_PREM;
      } else {
        // cash sale: dominated by cheap OTC + parapharmacie; premium is rare
        pool =
          roll < 0.62 ? POOL_FAST : roll < 0.82 ? POOL_PARA : roll < 0.96 ? POOL_CHRON : POOL_PREM;
      }
      let pid = pick(pool);
      let guard = 0;
      while (used.has(pid) && guard < 4) {
        pid = pick(pool);
        guard += 1;
      }
      used.add(pid);
      // Quantity: usually 1, sometimes 2, rarely 3.
      const q = rng() < 0.7 ? 1 : rng() < 0.9 ? 2 : 3;
      lines.push({ produit_id: pid, quantite: q });
    }
    return lines;
  };

  // Weighted small basket size: most sales are 1–2 lines, occasionally 3, rarely 4.
  const basketSize = () => {
    const r = rng();
    return r < 0.5 ? 1 : r < 0.82 ? 2 : r < 0.95 ? 3 : 4;
  };

  // ── Procedural 45-day history ────────────────────────────────────────────
  // Algerian week: Fri/Sat are the weekend (lighter footfall). We bias volume
  // by weekday so the trend chart has natural rhythm, plus a gentle upward
  // trend toward today so the period-over-period comparison shows growth.
  const DAYS = 45;
  for (let d = DAYS; d >= 1; d -= 1) {
    const day = new Date();
    day.setDate(day.getDate() - d);
    const wd = day.getDay(); // 0=Sun … 5=Fri, 6=Sat
    const weekend = wd === 5 || wd === 6;
    const growth = 1 + (DAYS - d) / (DAYS * 2); // ~1.0 → ~1.5 toward today
    const base = weekend ? between(8, 13) : between(16, 24);
    const count = Math.max(1, Math.round(base * growth));
    for (let k = 0; k < count; k += 1) {
      const hour = between(8, 19);
      const min = between(0, 59);
      const isChifa = chance(0.3);
      const cais = caissiers.length > 1 && chance(0.45) ? caissier2 : caissier1;
      if (isChifa && chifaClients.length) {
        const cl = chifaClients[Math.floor(rng() * chifaClients.length)];
        ventes.push(
          mkVente("CHIFA", "VALIDEE", atDayHour(d, hour, min), cl, basket(basketSize(), { chifa: true }), { caissier: cais }),
        );
      } else {
        // ~35% of libre sales are linked to a known client (loyalty), rest walk-in.
        const cl = chance(0.35) ? allClients[Math.floor(rng() * allClients.length)] : null;
        const remise = chance(0.12) ? between(5, 15) : 0;
        ventes.push(
          mkVente("LIBRE", "VALIDEE", atDayHour(d, hour, min), cl, basket(basketSize()), { caissier: cais, remise_pct: remise }),
        );
      }
    }
  }

  // ── Today's activity (drives the open caisse + "aujourd'hui" KPIs) ────────
  const todayCount = between(14, 20);
  for (let k = 0; k < todayCount; k += 1) {
    const hour = between(8, Math.min(19, new Date().getHours() || 12));
    const isChifa = chance(0.3);
    const cais = chance(0.5) && caissier2 ? caissier2 : caissier1;
    if (isChifa && chifaClients.length) {
      const cl = chifaClients[Math.floor(rng() * chifaClients.length)];
      ventes.push(mkVente("CHIFA", "VALIDEE", atDayHour(0, hour, between(0, 59)), cl, basket(basketSize(), { chifa: true }), { caissier: cais }));
    } else {
      const cl = chance(0.35) ? allClients[Math.floor(rng() * allClients.length)] : null;
      ventes.push(mkVente("LIBRE", "VALIDEE", atDayHour(0, hour, between(0, 59)), cl, basket(basketSize()), { caissier: cais }));
    }
  }

  // ── Showcase rows — guarantee every type + statut is represented ──────────
  const showcase = [
    // INSTANCE (deferred) — a couple in the unified list (the dedicated debt
    // registry lives in instances.js; these make the list filter non-empty).
    mkVente("INSTANCE", "VALIDEE", atDayHour(1, 11, 20), clients[7], [{ produit_id: "p-018", quantite: 1 }, { produit_id: "p-043", quantite: 1 }], { carte_chifa_retenue: true, notes: "Patient repasse vendredi" }),
    mkVente("INSTANCE", "VALIDEE", atDayHour(3, 16, 5), clients[1], [{ produit_id: "p-011", quantite: 1 }, { produit_id: "p-019", quantite: 1 }], { notes: "Règlement à la fin du mois" }),
    // EN_COURS today (waiting for cashier validation) — POS resume demo.
    mkVente("LIBRE", "EN_COURS", atDayHour(0, 10, 30), null, [{ produit_id: "p-020", quantite: 1 }, { produit_id: "p-202", quantite: 1 }]),
    mkVente("CHIFA", "EN_COURS", atDayHour(0, 11, 45), chifaClients[0] || clients[0], [{ produit_id: "p-013", quantite: 1 }]),
    // ANNULEE (manager reversal) + RETOURNEE (linked to an avoir).
    mkVente("LIBRE", "ANNULEE", atDayHour(2, 9, 15), null, [{ produit_id: "p-001", quantite: 1 }], { notes: "Erreur de saisie — annulé par le manager" }),
    mkVente("LIBRE", "RETOURNEE", atDayHour(8, 14, 0), clients[5], [{ produit_id: "p-204", quantite: 1 }], { notes: "Retour produit — avoir AV-2026-005" }),
  ].filter(Boolean);

  return [...ventes, ...showcase];
}
